// Flying Saucer: An Interstellar Teahouse — application bootstrap & main loop.
import { Renderer } from './engine/renderer.js';
import { audio } from './engine/audio.js';
import { input } from './engine/input.js';
import { initModels } from './game/models.js';
import { initAssets } from './engine/assets.js';
import { LEVELS, starGoals } from './game/data.js';
import { Game } from './game/game.js';
import { HUD } from './ui/hud.js';
import { Screens, emptyCrewNote } from './ui/screens.js';
import { MenuScene } from './ui/menuScene.js';
import { buildIcons } from './ui/icons.js';
import { setCoachIcons } from './game/coach.js';
import { buildShowcase } from './debug/showcase.js';
import * as ITEMS from './game/items.js';
import { Online, codeFromUrl } from './net/room.js';
import { isBot, makeRoomForPerson, refillBots, crewKind } from './game/helper.js';

const SAVE_KEY = 'flying-saucer-teahouse-v1';

class App {
  constructor() {
    this.canvas = document.getElementById('gl');
    this.state = 'loading';
    this.paused = false;
    // the chefs on this screen, in crew slot order: people by their device, bot helpers as 'bot1'..'bot3'
    this.players = [{ device: 'kbA' }];
    // people on this screen watching instead of cooking: no chef, but they own bots and steer them with Delegate
    this.spectators = [];
    this.online = null; // an online room, while hosting or joined (see src/net/room.js)
    this.save = this._load();
    // the bot helpers you had last time come back with you
    for (let i = 1; i <= Math.min(3, this.save.bots || 0); i++) this.players.push({ device: 'bot' + i });
    // and if you were watching a crew of bots, you watch it again
    if (this.save.spectate && this.players.some(isBot)) this.spectate('kbA');
    this.audio = audio;
  }
  /** Remember how many bot helpers are in the crew (kept between sessions, like scores). */
  saveBots(n) {
    this.save.bots = Math.max(0, Math.min(3, n));
    this.persist();
  }
  /** Remember whether you watch rather than cook (kept between sessions, like the bot count). */
  saveSpectating(on) {
    this.save.spectate = !!on;
    this.persist();
  }

  // ------------------------------------------------------------ crew on this screen
  /** Nobody on this screen cooks: a crew of bots, watched. */
  get onlyBotsCook() {
    return !this.players.some((p) => !isBot(p));
  }
  /** A cook here steps out of the crew to watch instead. False when theirs is the last chef: a crew always keeps one. */
  spectate(device) {
    const i = this.players.findIndex((p) => p.device === device);
    if (i < 0 || this.players.length <= 1) return false;
    this.players.splice(i, 1);
    this.spectators.push({ device });
    // a bot that gave up its place for a person takes the reopened seat back
    refillBots(this.players, this.save.bots || 0, (dev) => ({ device: dev }));
    this.saveSpectating(this.onlyBotsCook);
    return true;
  }
  /**
   * Someone here takes a chef: a spectator stepping back in, or a new player joining. A full crew gives up its last bot
   * for them (the saved bot count stays, so that bot returns when the seat reopens). False when four people already cook.
   */
  cook(device) {
    if (this.players.some((p) => p.device === device)) return false;
    if (!makeRoomForPerson(this.players)) return false;
    this.spectators = this.spectators.filter((p) => p.device !== device);
    this.players.push({ device });
    this.saveSpectating(false);
    return true;
  }
  /** A spectator here leaves altogether. False for the last person on this screen, who stays to watch (or cook). */
  leave(device) {
    const people = this.players.filter((p) => !isBot(p)).length + this.spectators.length;
    if (people <= 1 || !this.spectators.some((p) => p.device === device)) return false;
    this.spectators = this.spectators.filter((p) => p.device !== device);
    return true;
  }
  _load() {
    const def = { stars: {}, best: {}, settings: { master: 0.8, music: 0.6, sfx: 0.85, quality: 'high', shake: true, lens: true, coach: true, padStyle: 'auto' }, delegate: {}, bots: 0, spectate: false, bestInfinite: {} };
    try {
      const s = JSON.parse(localStorage.getItem(SAVE_KEY) || 'null');
      if (s) {
        // shift 4 used to be the Zero-G Deck: carry its stars over so later shifts stay unlocked
        if (s.stars && s.stars['zero-g'] != null) s.stars['boba-orbit'] = Math.max(s.stars['boba-orbit'] || 0, s.stars['zero-g']);
        if (s.stars) delete s.stars['zero-g'];
        if (s.best) delete s.best['zero-g'];
        const save = { ...def, ...s, settings: { ...def.settings, ...(s.settings || {}) } };
        // a save from before best scores were kept per kind of crew: each old best goes to the kind it last played with
        const legacy = s.spectate && s.bots ? 'bots' : s.bots ? 'mixed' : 'humans';
        for (const all of [save.best, save.bestInfinite]) for (const id in all) if (typeof all[id] === 'number') all[id] = { [legacy]: all[id] };
        return save;
      }
    } catch (e) {}
    return def;
  }
  persist() {
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(this.save));
    } catch (e) {}
  }
  totalStars() {
    return Object.values(this.save.stars).reduce((a, b) => a + b, 0);
  }
  isUnlocked(i) {
    if (i === 0) return true;
    if (new URLSearchParams(location.search).has('unlock')) return true;
    return (this.save.stars[LEVELS[i - 1].id] || 0) >= 1;
  }
  /** A shift's best score for one kind of crew ('humans', 'mixed' or 'bots'), timed or infinite. */
  bestFor(id, kind, infinite = false) {
    const t = (infinite ? this.save.bestInfinite || {} : this.save.best)[id];
    return (t && t[kind]) || 0;
  }
  /** Raise a shift's best for one kind of crew to this score. True when it beat the best. */
  raiseBest(id, kind, infinite, coins) {
    const all = infinite ? (this.save.bestInfinite = this.save.bestInfinite || {}) : this.save.best;
    const t = (all[id] = all[id] || {});
    if (coins <= (t[kind] || 0)) return false;
    t[kind] = coins;
    return true;
  }
  resetProgress() {
    this.save.stars = {};
    this.save.best = {};
    this.save.bestInfinite = {};
    this.persist();
  }
  applySettings() {
    const S = this.save.settings;
    audio.vol.master = S.master;
    audio.vol.music = S.music;
    audio.vol.sfx = S.sfx;
    audio.applyVolumes();
    if (this.renderer.quality !== S.quality) this.renderer.setQuality(S.quality);
    if (this.game) this.game.coachOn = S.coach !== false;
    input.padStyleSetting = S.padStyle || 'auto';
    this.persist();
  }

  async boot() {
    const bar = document.querySelector('#loading .bar i');
    const setP = (p, t) => {
      bar.style.width = p * 100 + '%';
      if (t) document.querySelector('#loading .lt').textContent = t;
    };
    try {
      this.renderer = new Renderer(this.canvas);
    } catch (e) {
      document.querySelector('#loading .lt').textContent = 'Sorry! This game needs WebGL2. ' + e.message;
      return;
    }
    const qp = new URLSearchParams(location.search).get('q');
    if (qp) this.save.settings.quality = qp;
    this.renderer.setQuality(this.save.settings.quality);
    initModels(this.renderer.gl);
    initAssets(this.renderer.gl);
    setP(0.2, 'Warming up the kettles…');
    await frame();
    this.menu = new MenuScene();
    setP(0.45, 'Painting the nebula…');
    await frame();
    this.icons = buildIcons(this.renderer);
    setCoachIcons(this.icons);
    setP(0.8, 'Polishing teacups…');
    await frame();
    // prime the menu render (compiles shaders)
    this.renderer.render(this.menu.scene, this.menu.camera, 0.016);
    setP(1, 'Ready for launch');
    this.hud = new HUD(this);
    this.screens = new Screens(this);
    this.state = 'menu';
    await frame();
    document.getElementById('loading').classList.add('done');
    setTimeout(() => document.getElementById('loading').remove(), 1200);
    // splash then title
    const go = () => {
      if (this._started) return;
      this._started = true;
      audio.init();
      this.applySettings();
      audio.playSong('menu');
      this.screens.show('title', { replace: true });
      // an invite link goes straight into its room
      const code = codeFromUrl();
      if (code) this.goOnline(code);
    };
    this.screens.show('splash', { onGo: go });
    window.addEventListener('mousemove', (e) => {
      if (this.menu) this.menu.setMouse(e.clientX / innerWidth - 0.5, e.clientY / innerHeight - 0.5);
    });
    input.onPress((code) => {
      if (this.state !== 'game') return;
      if (this._resumeFrame === this._frameNo) return;
      if (code === 'Escape' || code === 'KeyP' || /^Pad\d+:9$/.test(code)) {
        if (this.state === 'results') return;
        // the Delegate wheel is open: this just closes it (nothing gets switched)
        if (!this.paused && this.game && this.game.cancelWheel()) return;
        if (!this.paused) this.pause();
        else if (code !== 'Escape') this.resume();
      }
    });
    // F3: frame-rate overlay for diagnosing performance
    window.addEventListener('keydown', (e) => {
      if (e.code !== 'F3') return;
      e.preventDefault();
      if (this._fpsEl) { this._fpsEl.remove(); this._fpsEl = null; }
      else { this._fpsEl = document.createElement('div'); this._fpsEl.className = 'fps'; document.body.appendChild(this._fpsEl); }
    });
    window.addEventListener('blur', () => {
      // online, stepping away pauses nobody else's shift; an infinite shift keeps going (it plays on a party screen)
      if (this.state === 'game' && !this.paused && !this.online && this.game && this.game.state === 'play' && !this.game.infinite) this.pause();
    });
    this.last = performance.now();
    this._keepSteppingWhenHidden();
    requestAnimationFrame((t) => this.loop(t));
  }

  /**
   * Browsers stop animation frames in a hidden tab. Online, that would freeze the host's kitchen for
   * every guest (and stall a guest's own chef), so while online a worker's timer (which keeps running
   * in hidden tabs) steps the game whenever real frames stop coming. Nothing is drawn meanwhile.
   */
  _keepSteppingWhenHidden() {
    this._lastFrame = performance.now();
    try {
      const src = URL.createObjectURL(new Blob(['setInterval(() => postMessage(0), 16);'], { type: 'text/javascript' }));
      const ticker = new Worker(src);
      ticker.onmessage = () => {
        if (this.online && performance.now() - this._lastFrame > 100) this.loop(performance.now(), true);
      };
    } catch (e) {
      // no workers here: online play then pauses along with the tab, as offline play does
    }
  }

  /** fromHost: an online guest following the host into a shift. infinite: a shift with no closing time. */
  startLevel(i, { fromHost = false, infinite = false } = {}) {
    const net = this.online;
    // a shift needs someone at the stoves: a room whose last chef just left has none until someone cooks or a bot joins
    if (!(net ? net.crew : this.players).length) return this.screens.toast(emptyCrewNote(true));
    document.body.classList.remove('paused');
    audio.init();
    if (this.game) this.game.dispose();
    this.paused = false;
    this.screens.clear();
    document.body.classList.add('ingame');
    if (net && net.isHost && !fromHost) net.startShift(i, infinite);
    // online, every browser seats the same crew (and spectators) in the same order; each knows which people are its own
    const seat = (p) => ({ peer: p.peer, dev: p.dev, device: p.peer === net.peer ? p.dev : 'net:' + p.peer + ':' + p.dev });
    const players = net ? net.crew.map(seat) : this.players.slice();
    const spectators = net ? net.spectators.map(seat) : this.spectators.slice();
    this.game = new Game(this, i, players, net, spectators, { infinite });
    // (the best from before this run, for the results card: an infinite run keeps the saved best as it climbs)
    if (infinite) this.game._bestBefore = this.bestFor(this.game.level.id, crewKind(players), true);
    this.game.shakeEnabled = this.save.settings.shake;
    this.state = 'game';
    audio.playSong(this.game.level.id);
    audio.intensity = 0;
    audio.tempoMul = 1;
  }
  /** fromNet: the room told us (online, pausing and resuming happen for everyone together). */
  pause(fromNet = false) {
    if (!this.game || this.game.state === 'end' || this.paused) return;
    this.game.cancelWheel();
    if (this.online) {
      if (!fromNet || this.online.isHost) this.online.setPaused(true);
      if (!fromNet && !this.online.isHost) return;
    }
    this.paused = true;
    document.body.classList.add('paused');
    audio.stopLoops();
    audio.playSong('pause');
    this.screens.show('pause', { replace: true, onBack: () => this.resume() });
  }
  resume(fromNet = false) {
    if (this.online) {
      if (!fromNet || this.online.isHost) this.online.setPaused(false);
      if (!fromNet && !this.online.isHost) return;
    }
    this.paused = false;
    if (this.game && this.game.state !== 'end') audio.playSong(this.game.level.id);
    document.body.classList.remove('paused');
    this._resumeFrame = this._frameNo;
    this.screens.clear();
    this.last = performance.now();
  }
  quitToMap() {
    if (this.online && !this.online.isHost) return this.leaveOnline();
    if (this.online) this.online.backToLobby();
    document.body.classList.remove('paused');
    if (this.game) this.game.dispose();
    this.game = null;
    this.paused = false;
    this.state = 'menu';
    this.hud.hide();
    document.body.classList.remove('ingame');
    audio.playSong('map');
    this.screens.show(this.online ? 'online' : 'title', { replace: true });
    this.screens.show('map');
  }
  /**
   * An infinite shift can run all night, so its best score is saved as it climbs (every few seconds), not only when it
   * ends: a closed tab or a reload still keeps the night's best.
   */
  _keepInfiniteBest(g, dt) {
    if ((g._bestT = (g._bestT || 0) - dt) > 0) return;
    g._bestT = 5;
    if (this.raiseBest(g.level.id, crewKind(g.players), true, g.coins)) this.persist();
  }
  /** End an infinite shift from the pause menu: the kitchen stops, and its score is tallied. */
  endInfinite() {
    const g = this.game;
    if (!g || !g.infinite || g.state === 'end' || (this.online && !this.online.isHost)) return;
    g.state = 'end';
    g.stateT = 0;
    this.finishLevel();
  }
  /** results: an online guest gets them from the host. */
  finishLevel(results = null) {
    const g = this.game;
    if (!g) return;
    const L = g.level;
    const coins = results ? results.coins : g.coins;
    const infinite = results ? !!results.infinite : g.infinite;
    const goals = starGoals(L, g.players.length);
    // an infinite shift earns no stars, and its best score is kept apart from the timed best
    const stars = infinite ? 0 : goals.filter((s) => coins >= s).length;
    // best scores are kept apart for all-human, mixed and all-bot crews: this one counts for the crew that cooked
    const kind = results && results.crew ? results.crew : crewKind(g.players);
    const prevBest = infinite && g._bestBefore !== undefined ? g._bestBefore : this.bestFor(L.id, kind, infinite);
    const newBest = coins > prevBest;
    this.raiseBest(L.id, kind, infinite, coins);
    if (!infinite) this.save.stars[L.id] = Math.max(this.save.stars[L.id] || 0, stars);
    this.persist();
    this.state = 'results';
    this.paused = false;
    document.body.classList.remove('paused');
    this.hud.hide();
    audio.stopLoops();
    audio.playSong('results');
    const res = results || { levelIndex: g.levelIndex, coins: g.coins, served: g.served, failed: g.failed, tips: g.tips, goals, infinite, played: g.played, crew: kind };
    if (this.online && this.online.isHost) this.online.shiftEnded(res);
    this.screens.show('results', { replace: true, ...res, goals, stars, newBest: newBest && prevBest > 0 });
  }

  // ------------------------------------------------------------ online
  /** Open the online lobby: host a new room, or join one by its code. */
  goOnline(code = null) {
    if (this.online) this.online.leave();
    this.online = new Online(this);
    if (code) this.online.join(code);
    else this.online.host();
    this.screens.show('online', { replace: true });
  }
  /** Leave the room (and any shift in it) for the title screen. */
  leaveOnline() {
    if (this.online) this.online.leave();
    this.online = null;
    this._toTitle();
  }
  /** The room closed under us (host left, bad code, connection lost). */
  onlineEnded(why) {
    this.online = null;
    this._toTitle();
    const msg = { 'no-room': 'That room is not open. Check the code with your host.', full: 'That room is full.', 'host-left': 'The host closed the room.', lost: 'Lost the connection to the room.', taken: 'Could not open a room. Try again.' }[why] || 'The room closed.';
    this.screens.toast(msg);
  }
  /** Online guest: the host went back to picking a shift. */
  toOnlineLobby() {
    this._leaveShift();
    audio.playSong('map');
    this.screens.show('online', { replace: true });
  }
  _toTitle() {
    this._leaveShift();
    audio.playSong('menu');
    if (location.hash) history.replaceState(null, '', location.pathname + location.search);
    this.screens.show('title', { replace: true });
  }
  _leaveShift() {
    if (this.game) this.game.dispose();
    this.game = null;
    this.paused = false;
    this.state = 'menu';
    document.body.classList.remove('paused', 'ingame');
    this.hud.hide();
    audio.stopLoops();
  }

  /** Debug: model lineup under game lighting (before/after comparisons). */
  showcase(kind) {
    this.screens.clear();
    this.hud.hide();
    this.state = 'showcase';
    this._show = buildShowcase(kind);
  }

  /** If the first seconds of a shift run slowly, step graphics quality down once per shift. */
  _adaptQuality(dt) {
    const g = this.game;
    if (!g || g._qDone || g.state === 'intro') return;
    g._qT = (g._qT || 0) + dt;
    g._qN = (g._qN || 0) + 1;
    if (g._qT < 4) return;
    g._qDone = true;
    const avg = g._qT / g._qN;
    const order = ['low', 'medium', 'high', 'ultra'];
    const i = order.indexOf(this.save.settings.quality);
    if (avg > 1 / 38 && i > 0) {
      this.save.settings.quality = order[i - 1];
      this.applySettings();
      this.hud.banner('Graphics adjusted', `Quality set to ${order[i - 1]} for smoother play`, '#7af0ff');
    }
  }

  /** hidden: stepped by the background ticker (see _keepSteppingWhenHidden), so nothing is drawn. */
  loop(now, hidden = false) {
    const rawMs = now - this.last;
    const dt = Math.min(0.05, rawMs / 1000);
    if (!hidden) this._lastFrame = performance.now();
    if (this.renderer && !hidden && rawMs > 0 && rawMs < 250) this.renderer.adaptResolution(rawMs);
    if (this._fpsEl) {
      this._fpsAcc = (this._fpsAcc || 0) * 0.95 + rawMs * 0.05;
      if (this._frameNo % 15 === 0) this._fpsEl.textContent = `${Math.round(1000 / this._fpsAcc)} fps · ${this.renderer.w}×${this.renderer.h} · ${this.save.settings.quality}`;
    }
    this.last = now;
    this._frameNo = (this._frameNo || 0) + 1;
    input.poll();
    this.screens && this.screens.tick();
    const r = this.renderer;
    const lens = this.save.settings.lens;
    if ((this.state === 'game' || this.state === 'results') && this.game) {
      const g = this.game;
      if (!this.paused) {
        g.update(dt);
        if (g.infinite && g.state === 'play') this._keepInfiniteBest(g, dt);
        if (g.net) g.net.tick(g, dt);
        if (!hidden) this._adaptQuality(dt);
        if (!this.save.settings.shake) g.shake = 0;
        if (g.state === 'end' && g.stateT > 2.6 && this.state === 'game' && !g.mirror) this.finishLevel();
      }
      g.scene.env.grain = lens ? 0.03 : 0;
      g.scene.env.aberration = lens ? 0.01 : 0;
      g.scene.desat = this.paused ? 0.6 : 0;
      if (!hidden) r.render(g.scene, g.camera, this.paused ? 0 : dt);
    } else if (hidden) {
      // menus need no stepping while hidden
    } else if (this.state === 'showcase' && this._show) {
      r.render(this._show.scene, this._show.camera, dt);
    } else if (this.menu) {
      this.menu.update(dt);
      this.menu.scene.env.grain = lens ? 0.035 : 0;
      this.menu.scene.env.aberration = lens ? 0.02 : 0;
      r.render(this.menu.scene, this.menu.camera, dt);
    }
    input.endFrame();
    if (!hidden) requestAnimationFrame((t) => this.loop(t));
  }
}

const frame = () => new Promise((r) => requestAnimationFrame(() => r()));

const app = new App();
window.__app = app;
window.__items = ITEMS;
window.__input = input;
app.boot().catch((e) => {
  console.error(e);
  const lt = document.querySelector('#loading .lt');
  if (lt) lt.textContent = 'Something went wrong: ' + e.message;
});